'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Send, SmilePlus, Hash, Users, TrendingUp, ArrowLeft, ChevronDown, Shield, Pin, VolumeX, Trash2, X, ChevronUp, AlertTriangle, Radio, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface ChatMessage {
  id: string;
  user: string;
  flag: string;
  country: string;
  avatar: string;
  text: string;
  time: string;
  reactions: { emoji: string; count: number }[];
  isGoalAlert?: boolean;
  isPinned?: boolean;
  isRemoved?: boolean;
  roomId?: string | null;
}

interface ChatRoom {
  id: string;
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
  homeFlag: string;
  awayFlag: string;
  matchStatus: string;
  matchDate: string | null;
  gameEndedAt: string | null;
  deleteAt: string | null;
  createdAt: string;
}

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  msgId: string;
  user: string;
}

// DB row → ChatMessage
function rowToMessage(row: Record<string, unknown>): ChatMessage {
  const createdAt = row.created_at ? new Date(row.created_at as string) : new Date();
  const time = createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  let reactions: { emoji: string; count: number }[] = [];
  try {
    const raw = row.reactions;
    if (Array.isArray(raw)) reactions = raw as { emoji: string; count: number }[];
    else if (typeof raw === 'string') reactions = JSON.parse(raw);
  } catch {}
  return {
    id: row.id as string,
    user: (row.user_name as string) || 'Fan',
    flag: (row.flag as string) || '🌍',
    country: (row.country as string) || '',
    avatar: (row.avatar as string) || 'FN',
    text: row.text as string,
    time,
    reactions,
    isGoalAlert: (row.is_goal_alert as boolean) || false,
    isPinned: (row.is_pinned as boolean) || false,
    isRemoved: (row.is_removed as boolean) || false,
    roomId: (row.room_id as string) || null,
  };
}

function rowToRoom(row: Record<string, unknown>): ChatRoom {
  return {
    id: row.id as string,
    fixtureId: row.fixture_id as number,
    homeTeam: row.home_team as string,
    awayTeam: row.away_team as string,
    homeFlag: row.home_flag as string,
    awayFlag: row.away_flag as string,
    matchStatus: row.match_status as string,
    matchDate: row.match_date as string | null,
    gameEndedAt: row.game_ended_at as string | null,
    deleteAt: row.delete_at as string | null,
    createdAt: row.created_at as string,
  };
}

const LIVE_STATUSES = ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'LIVE'];
const FINISHED_STATUSES = ['FT', 'AET', 'PEN'];

function getRoomStatusLabel(status: string): { label: string; color: string; dot: string } {
  if (LIVE_STATUSES.includes(status)) return { label: 'LIVE', color: 'text-red-400', dot: 'bg-red-400' };
  if (FINISHED_STATUSES.includes(status)) return { label: 'FT', color: 'text-slate-400', dot: 'bg-slate-500' };
  return { label: 'Soon', color: 'text-yellow-400', dot: 'bg-yellow-400' };
}

const trendingTopics = [
  { id: 'trend-001', tag: '#ViniciusJr', count: '12.4k' },
  { id: 'trend-002', tag: '#BRAvsARG', count: '9.8k' },
  { id: 'trend-003', tag: '#WorldCup2026', count: '8.1k' },
  { id: 'trend-004', tag: '#Raphinha', count: '5.2k' },
  { id: 'trend-005', tag: '#LMartinez', count: '4.7k' },
  { id: 'trend-006', tag: '#MetLifeStadium', count: '2.3k' },
];

const quickReactions = ['🔥', '⚽', '😱', '👏', '🇧🇷', '🇦🇷', '💪', '😂'];

// ─── Admin Context Menu ───────────────────────────────────────────────────────
function AdminContextMenu({
  menu, isPinned, isMuted, onPin, onMute, onRemove, onClose,
}: {
  menu: ContextMenu; isPinned: boolean; isMuted: boolean;
  onPin: () => void; onMute: () => void; onRemove: () => void; onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div ref={ref} className="fixed z-50 rounded-xl shadow-2xl overflow-hidden min-w-[180px]"
      style={{ top: menu.y, left: menu.x, background: 'hsl(222 35% 13%)', border: '1px solid hsl(222 25% 22%)' }}>
      <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 18%)' }}>
        <Shield size={12} className="text-orange-400" />
        <span className="text-xs font-semibold text-orange-400">{t.liveChat.adminControls}</span>
      </div>
      <div className="py-1">
        <button onClick={onPin} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors hover:bg-white/5"
          style={{ color: isPinned ? 'hsl(45 95% 60%)' : 'hsl(222 15% 75%)' }}>
          <Pin size={14} className={isPinned ? 'text-yellow-400' : 'text-slate-400'} />
          {isPinned ? t.liveChat.unpinMessage : t.liveChat.pinMessage}
        </button>
        <button onClick={onMute} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors hover:bg-white/5"
          style={{ color: isMuted ? 'hsl(200 80% 60%)' : 'hsl(222 15% 75%)' }}>
          <VolumeX size={14} className={isMuted ? 'text-blue-400' : 'text-slate-400'} />
          {isMuted ? t.liveChat.unmuteUser : t.liveChat.muteUser}
        </button>
        <div style={{ borderTop: '1px solid hsl(222 25% 18%)', margin: '4px 0' }} />
        <button onClick={onRemove} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors hover:bg-red-500/10"
          style={{ color: 'hsl(0 84% 65%)' }}>
          <Trash2 size={14} className="text-red-400" />
          {t.liveChat.removeMessage}
        </button>
      </div>
    </div>
  );
}

// ─── Pinned Message Banner ────────────────────────────────────────────────────
function PinnedMessageBanner({ message, onUnpin, isAdmin }: { message: ChatMessage; onUnpin: () => void; isAdmin: boolean }) {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useLanguage();
  return (
    <div className="flex items-start gap-3 px-4 py-2.5 flex-shrink-0"
      style={{ background: 'hsl(45 95% 53% / 0.08)', borderBottom: '1px solid hsl(45 95% 53% / 0.2)' }}>
      <Pin size={13} className="text-yellow-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-semibold text-yellow-400">{t.liveChat.pinnedMessage}</span>
          <span className="text-xs text-slate-500">{t.liveChat.by} {message.user}</span>
        </div>
        {!collapsed && <p className="text-xs text-slate-300 leading-relaxed truncate">{message.text}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => setCollapsed((c) => !c)} className="p-1 rounded text-slate-500 hover:text-slate-300 transition-colors">
          {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
        </button>
        {isAdmin && (
          <button onClick={onUnpin} className="p-1 rounded text-slate-500 hover:text-red-400 transition-colors">
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Muted Users Panel ────────────────────────────────────────────────────────
function MutedUsersPanel({ mutedUsers, onUnmute, onClose }: { mutedUsers: string[]; onUnmute: (user: string) => void; onClose: () => void }) {
  const { t } = useLanguage();
  return (
    <div className="absolute top-14 right-4 z-40 rounded-xl shadow-2xl min-w-[220px] overflow-hidden"
      style={{ background: 'hsl(222 35% 13%)', border: '1px solid hsl(222 25% 22%)' }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(222 25% 18%)' }}>
        <div className="flex items-center gap-2">
          <VolumeX size={14} className="text-blue-400" />
          <span className="text-sm font-semibold text-white">{t.liveChat.mutedUsers}</span>
          <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'hsl(200 80% 50% / 0.15)', color: 'hsl(200 80% 60%)' }}>
            {mutedUsers.length}
          </span>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X size={14} /></button>
      </div>
      <div className="py-2 max-h-48 overflow-y-auto">
        {mutedUsers.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-4">{t.liveChat.noMutedUsers}</p>
        ) : (
          mutedUsers.map((user) => (
            <div key={user} className="flex items-center justify-between px-4 py-2 hover:bg-white/4 transition-colors">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: 'hsl(222 25% 20%)', color: 'hsl(222 15% 55%)' }}>
                  {user.slice(0, 2).toUpperCase()}
                </div>
                <span className="text-sm text-slate-300">{user}</span>
              </div>
              <button onClick={() => onUnmute(user)} className="text-xs font-medium px-2 py-1 rounded-lg transition-colors hover:bg-blue-500/20"
                style={{ color: 'hsl(200 80% 60%)' }}>
                {t.liveChat.unmute}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────────────────────────
function MessageBubble({ msg, onReact, isAdmin, isMuted, onContextMenu }: {
  msg: ChatMessage; onReact: (msgId: string, emoji: string) => void;
  isAdmin: boolean; isMuted: boolean; onContextMenu: (e: React.MouseEvent, msgId: string, user: string) => void;
}) {
  const { t } = useLanguage();
  if (msg.isRemoved) {
    if (!isAdmin) return null;
    return (
      <div className="flex items-center gap-2 px-1 py-1 opacity-40">
        <AlertTriangle size={12} className="text-red-400 flex-shrink-0" />
        <span className="text-xs text-slate-600 italic">{t.liveChat.messageRemovedByAdmin}</span>
      </div>
    );
  }

  if (msg.isGoalAlert) {
    return (
      <div className="message-slide-in flex justify-center my-3">
        <div className="flex flex-col items-center gap-2 px-5 py-3 rounded-xl max-w-md w-full"
          style={{ background: 'hsl(25 95% 53% / 0.12)', border: '1px solid hsl(25 95% 53% / 0.3)' }}>
          <span className="text-2xl">⚽</span>
          <p className="text-sm font-bold text-orange-300 text-center">{msg.text}</p>
          <p className="text-xs text-slate-500">{msg.time}</p>
          <div className="flex gap-2 flex-wrap justify-center">
            {msg.reactions.map((r) => (
              <button key={`${msg.id}-react-${r.emoji}`} onClick={() => onReact(msg.id, r.emoji)}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-all hover:scale-105"
                style={{ background: 'hsl(222 25% 18%)', color: 'hsl(222 15% 65%)' }}>
                {r.emoji} <span className="font-mono tabular-nums">{r.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`message-slide-in flex gap-3 group px-1 rounded-lg transition-all ${isMuted ? 'opacity-30' : ''} ${msg.isPinned ? 'bg-yellow-500/5 border border-yellow-500/10 rounded-lg px-2 py-1' : ''}`}
      onContextMenu={isAdmin ? (e) => onContextMenu(e, msg.id, msg.user) : undefined}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1"
        style={{ background: 'hsl(222 25% 20%)', color: 'hsl(222 15% 65%)' }}>
        {msg.flag}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-white">{msg.user}</span>
          {msg.country && <span className="text-xs text-slate-600">{msg.country}</span>}
          {isMuted && (
            <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'hsl(200 80% 50% / 0.12)', color: 'hsl(200 80% 60%)' }}>
              <VolumeX size={9} /> {t.liveChat.muteUser}
            </span>
          )}
          {msg.isPinned && (
            <span className="flex items-center gap-1 text-xs" style={{ color: 'hsl(45 95% 60%)' }}>
              <Pin size={9} /> {t.liveChat.pinnedMessage}
            </span>
          )}
          <span className="text-xs text-slate-600 font-mono tabular-nums ml-auto opacity-0 group-hover:opacity-100 transition-opacity">{msg.time}</span>
          {isAdmin && (
            <button onClick={(e) => onContextMenu(e, msg.id, msg.user)}
              className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 p-0.5 rounded text-slate-600 hover:text-orange-400">
              <Shield size={11} />
            </button>
          )}
        </div>
        <p className="text-sm text-slate-300 leading-relaxed">{msg.text}</p>
        {msg.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {msg.reactions.map((r) => (
              <button key={`${msg.id}-react-${r.emoji}`} onClick={() => onReact(msg.id, r.emoji)}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-all hover:scale-105"
                style={{ background: 'hsl(222 25% 16%)', border: '1px solid hsl(222 25% 20%)', color: 'hsl(222 15% 65%)' }}>
                {r.emoji} <span className="font-mono tabular-nums">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Room List ────────────────────────────────────────────────────────────────
function RoomList({ rooms, activeRoomId, onSelect, onSync, syncing }: {
  rooms: ChatRoom[]; activeRoomId: string | null;
  onSelect: (room: ChatRoom | null) => void; onSync: () => void; syncing: boolean;
}) {
  const { t } = useLanguage();
  const liveRooms = rooms.filter((r) => LIVE_STATUSES.includes(r.matchStatus));
  const upcomingRooms = rooms.filter((r) => !LIVE_STATUSES.includes(r.matchStatus) && !FINISHED_STATUSES.includes(r.matchStatus));
  const finishedRooms = rooms.filter((r) => FINISHED_STATUSES.includes(r.matchStatus));

  const RoomItem = ({ room }: { room: ChatRoom }) => {
    const { label, color, dot } = getRoomStatusLabel(room.matchStatus);
    const isActive = room.id === activeRoomId;
    return (
      <button onClick={() => onSelect(room)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 text-left ${isActive ? 'bg-orange-500/10' : 'hover:bg-white/4'}`}>
        <span className="text-base flex-shrink-0">{room.homeFlag}{room.awayFlag}</span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${isActive ? 'text-orange-300' : 'text-slate-300'}`}>
            {room.homeTeam} vs {room.awayTeam}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
            <span className={`text-xs font-mono ${color}`}>{label}</span>
          </div>
        </div>
        {isActive && <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />}
      </button>
    );
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
        <div className="flex items-center gap-2">
          <Hash size={15} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-white">{t.liveChat.gameRooms}</h3>
          {rooms.length > 0 && (
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'hsl(25 95% 53% / 0.15)', color: 'hsl(25 95% 60%)' }}>
              {rooms.length}
            </span>
          )}
        </div>
        <button onClick={onSync} disabled={syncing} title="Sync game rooms"
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-40">
          <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
        </button>
      </div>
      <div className="px-3 py-3 space-y-1 max-h-80 overflow-y-auto">
        {/* General room */}
        <button onClick={() => onSelect(null)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 text-left ${activeRoomId === null ? 'bg-orange-500/10' : 'hover:bg-white/4'}`}>
          <span className="text-base flex-shrink-0">🌍</span>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${activeRoomId === null ? 'text-orange-300' : 'text-slate-300'}`}>{t.liveChat.generalChat}</p>
            <p className="text-xs text-slate-500">{t.chat.online}</p>
          </div>
          {activeRoomId === null && <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />}
        </button>

        {liveRooms.length > 0 && (
          <>
            <p className="text-xs font-semibold text-red-400 px-3 pt-2 pb-1 flex items-center gap-1.5">
              <Radio size={10} className="animate-pulse" /> {t.common.live}
            </p>
            {liveRooms.map((r) => <RoomItem key={r.id} room={r} />)}
          </>
        )}
        {upcomingRooms.length > 0 && (
          <>
            <p className="text-xs font-semibold text-yellow-400 px-3 pt-2 pb-1 flex items-center gap-1.5">
              <Clock size={10} /> {t.common.upcoming}
            </p>
            {upcomingRooms.map((r) => <RoomItem key={r.id} room={r} />)}
          </>
        )}
        {finishedRooms.length > 0 && (
          <>
            <p className="text-xs font-semibold text-slate-500 px-3 pt-2 pb-1">{t.common.finished}</p>
            {finishedRooms.map((r) => <RoomItem key={r.id} room={r} />)}
          </>
        )}
        {rooms.length === 0 && (
          <div className="px-3 py-4 text-center">
            <p className="text-xs text-slate-500">{t.liveChat.noRoomsAvailable}</p>
            <p className="text-xs text-slate-600 mt-1">{t.liveChat.roomsAppearHere}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LiveChatPanel() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [onlineCount] = useState(2847);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mutedUsers, setMutedUsers] = useState<string[]>([]);
  const [showMutedPanel, setShowMutedPanel] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenu>({ visible: false, x: 0, y: 0, msgId: '', user: '' });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();
  const { t } = useLanguage();

  const pinnedMessage = messages.find((m) => m.isPinned && !m.isRemoved) ?? null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isAtBottom) scrollToBottom();
  }, [messages, isAtBottom]);

  // ── Load chat rooms ────────────────────────────────────────────────────────
  const fetchRooms = useCallback(async () => {
    setRoomsLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_rooms')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) {
        setRooms(data.map(rowToRoom));
      }
    } catch (err) {
      console.error('Rooms fetch error:', err);
    } finally {
      setRoomsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // ── Load messages for active room ──────────────────────────────────────────
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setMessages([]);
      try {
        let query = supabase
          .from('live_chat_messages')
          .select('*')
          .order('created_at', { ascending: true })
          .limit(100);

        if (activeRoom) {
          query = query.eq('room_id', activeRoom.id);
        } else {
          query = query.is('room_id', null);
        }

        const { data, error } = await query;
        if (error) {
          console.error('Error fetching chat messages:', error.message);
        } else {
          setMessages((data || []).map(rowToMessage));
        }
      } catch (err) {
        console.error('Chat fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [activeRoom]);

  // ── Realtime subscription ──────────────────────────────────────────────────
  useEffect(() => {
    const channelName = activeRoom ? `live_chat_room_${activeRoom.id}` : 'live_chat_general';
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'live_chat_messages' }, (payload) => {
        const newMsg = rowToMessage(payload.new as Record<string, unknown>);
        const belongsHere = activeRoom ? newMsg.roomId === activeRoom.id : newMsg.roomId === null;
        if (!belongsHere) return;
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'live_chat_messages' }, (payload) => {
        const updated = rowToMessage(payload.new as Record<string, unknown>);
        setMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [activeRoom]);

  // ── Realtime rooms subscription ────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel('chat_rooms_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_rooms' }, () => {
        fetchRooms();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchRooms]);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setIsAtBottom(scrollHeight - scrollTop - clientHeight < 50);
  };

  const handleSend = async () => {
    if (!inputText.trim()) return;
    const text = inputText.trim();
    setInputText('');

    try {
      const insertData: Record<string, unknown> = {
        user_name: 'Fan Noticopa',
        flag: '🌍',
        country: 'Global',
        avatar: 'FN',
        text,
        is_goal_alert: false,
        reactions: [],
      };
      if (activeRoom) insertData.room_id = activeRoom.id;

      const { error } = await supabase.from('live_chat_messages').insert(insertData);
      if (error) {
        console.error('Error sending message:', error.message);
        toast.error('Failed to send message');
        setInputText(text);
      }
    } catch (err) {
      console.error('Send error:', err);
      toast.error('Failed to send message');
      setInputText(text);
    }
  };

  const handleReact = useCallback(async (msgId: string, emoji: string) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== msgId) return msg;
        const existing = msg.reactions.find((r) => r.emoji === emoji);
        const updatedReactions = existing
          ? msg.reactions.map((r) => r.emoji === emoji ? { ...r, count: r.count + 1 } : r)
          : [...msg.reactions, { emoji, count: 1 }];
        return { ...msg, reactions: updatedReactions };
      })
    );
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;
    const existing = msg.reactions.find((r) => r.emoji === emoji);
    const updatedReactions = existing
      ? msg.reactions.map((r) => r.emoji === emoji ? { ...r, count: r.count + 1 } : r)
      : [...msg.reactions, { emoji, count: 1 }];
    await supabase.from('live_chat_messages').update({ reactions: updatedReactions }).eq('id', msgId);
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleQuickReaction = (emoji: string) => setInputText((prev) => prev + emoji);

  const handleSyncRooms = async () => {
    setSyncing(true);
    try {
      const res = await fetch('/api/cron/sync-chat-rooms', { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Synced ${data.rooms_upserted ?? 0} game rooms`);
        await fetchRooms();
      } else {
        toast.error(data.error || 'Sync failed');
      }
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // ── Admin Actions ──────────────────────────────────────────────────────────
  const openContextMenu = (e: React.MouseEvent, msgId: string, user: string) => {
    e.preventDefault();
    const vw = window.innerWidth; const vh = window.innerHeight;
    const x = Math.min(e.clientX, vw - 208); const y = Math.min(e.clientY, vh - 168);
    setContextMenu({ visible: true, x, y, msgId, user });
  };
  const closeContextMenu = () => setContextMenu((c) => ({ ...c, visible: false }));

  const handlePin = async () => {
    const { msgId } = contextMenu;
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;
    const nowPinned = !msg.isPinned;
    const unpinAll = messages.filter((m) => m.isPinned && m.id !== msgId)
      .map((m) => supabase.from('live_chat_messages').update({ is_pinned: false }).eq('id', m.id));
    await Promise.all(unpinAll);
    await supabase.from('live_chat_messages').update({ is_pinned: nowPinned }).eq('id', msgId);
    toast.success(nowPinned ? 'Message pinned' : 'Message unpinned');
    closeContextMenu();
  };

  const handleMute = () => {
    const { user } = contextMenu;
    setMutedUsers((prev) => {
      if (prev.includes(user)) { toast.success(`${user} unmuted`); return prev.filter((u) => u !== user); }
      toast.success(`${user} muted`); return [...prev, user];
    });
    closeContextMenu();
  };

  const handleRemove = async () => {
    const { msgId } = contextMenu;
    await supabase.from('live_chat_messages').update({ is_removed: true, is_pinned: false }).eq('id', msgId);
    toast.success('Message removed');
    closeContextMenu();
  };

  const handleUnpin = async () => {
    const pinned = messages.filter((m) => m.isPinned);
    await Promise.all(pinned.map((m) => supabase.from('live_chat_messages').update({ is_pinned: false }).eq('id', m.id)));
    toast.success('Message unpinned');
  };

  const handleUnmute = (user: string) => { setMutedUsers((prev) => prev.filter((u) => u !== user)); toast.success(`${user} unmuted`); };

  const toggleAdmin = () => {
    setIsAdmin((v) => {
      const next = !v;
      toast.success(next ? 'Admin mode enabled' : 'Admin mode disabled');
      if (!next) { setShowMutedPanel(false); closeContextMenu(); }
      return next;
    });
  };

  const activeRoomStatus = activeRoom ? getRoomStatusLabel(activeRoom.matchStatus) : null;

  return (
    <div className="flex gap-6 h-[calc(100vh-8rem)]">
      {/* Admin Context Menu */}
      {isAdmin && contextMenu.visible && (
        <AdminContextMenu menu={contextMenu}
          isPinned={messages.find((m) => m.id === contextMenu.msgId)?.isPinned ?? false}
          isMuted={mutedUsers.includes(contextMenu.user)}
          onPin={handlePin} onMute={handleMute} onRemove={handleRemove} onClose={closeContextMenu} />
      )}

      {/* Muted Users Panel */}
      {isAdmin && showMutedPanel && (
        <MutedUsersPanel mutedUsers={mutedUsers} onUnmute={handleUnmute} onClose={() => setShowMutedPanel(false)} />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col rounded-xl overflow-hidden min-w-0 relative"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ background: 'hsl(222 28% 13%)', borderBottom: '1px solid hsl(222 25% 18%)' }}>
          <div className="flex items-center gap-3">
            <Link href="/live-games-dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft size={16} />
            </Link>
            {activeRoom ? (
              <div className="flex items-center gap-2">
                <span className="text-base">{activeRoom.homeFlag}</span>
                <span className="text-base">{activeRoom.awayFlag}</span>
                <div className="hidden sm:block">
                  <p className="text-xs font-semibold text-white">{activeRoom.homeTeam} vs {activeRoom.awayTeam}</p>
                  <div className="flex items-center gap-1.5">
                    {activeRoomStatus && (
                      <>
                        <span className={`w-1.5 h-1.5 rounded-full ${activeRoomStatus.dot} ${LIVE_STATUSES.includes(activeRoom.matchStatus) ? 'live-pulse' : ''}`} />
                        <span className={`text-xs font-bold ${activeRoomStatus.color}`}>{activeRoomStatus.label}</span>
                      </>
                    )}
                    <span className="text-xs text-slate-500">Game Room</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-base">🌍</span>
                <div className="hidden sm:block">
                  <p className="text-xs font-semibold text-white">General Chat</p>
                  <p className="text-xs text-slate-500">All fans · World Cup 2026</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Users size={13} className="text-green-400" />
              <span className="font-mono tabular-nums text-green-400 font-semibold">{onlineCount.toLocaleString()}</span>
              <span className="hidden sm:inline">online</span>
            </div>
            <Link href="/game-detail" className="hidden sm:flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 transition-colors font-medium">
              Match Stats →
            </Link>
            <button onClick={toggleAdmin}
              className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all ${isAdmin ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' : 'text-slate-500 hover:text-slate-300 border border-transparent hover:border-slate-600'}`}>
              <Shield size={13} />
              <span className="hidden sm:inline">Admin</span>
            </button>
            {isAdmin && (
              <button onClick={() => setShowMutedPanel((v) => !v)}
                className={`relative flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all border ${showMutedPanel ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'text-slate-400 hover:text-slate-200 border-slate-700 hover:border-slate-500'}`}>
                <VolumeX size={13} />
                {mutedUsers.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-xs font-bold flex items-center justify-center"
                    style={{ background: 'hsl(200 80% 50%)', color: 'white', fontSize: '9px' }}>
                    {mutedUsers.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Admin Mode Banner */}
        {isAdmin && (
          <div className="flex items-center gap-2 px-4 py-2 flex-shrink-0 text-xs"
            style={{ background: 'hsl(25 95% 53% / 0.08)', borderBottom: '1px solid hsl(25 95% 53% / 0.15)' }}>
            <Shield size={12} className="text-orange-400" />
            <span className="text-orange-300 font-medium">Admin Mode Active</span>
            <span className="text-slate-500">— Right-click or hover any message to pin, mute, or remove</span>
          </div>
        )}

        {/* Pinned Message Banner */}
        {pinnedMessage && <PinnedMessageBanner message={pinnedMessage} onUnpin={handleUnpin} isAdmin={isAdmin} />}

        {/* Messages Feed */}
        <div ref={chatContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
                <span className="text-xs text-slate-500">{t.chat.loading}</span>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 text-center">
                <span className="text-4xl">{activeRoom ? `${activeRoom.homeFlag}${activeRoom.awayFlag}` : '💬'}</span>
                <p className="text-sm font-medium text-slate-400">
                  {activeRoom ? `${activeRoom.homeTeam} vs ${activeRoom.awayTeam}` : t.liveChat.generalChat}
                </p>
                <p className="text-xs text-slate-600">{t.chat.signInToChat}</p>
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} onReact={handleReact} isAdmin={isAdmin}
                isMuted={mutedUsers.includes(msg.user)} onContextMenu={openContextMenu} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button */}
        {!isAtBottom && (
          <div className="absolute bottom-32 right-8">
            <button onClick={scrollToBottom}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full shadow-lg transition-all"
              style={{ background: 'hsl(25 95% 53%)', color: 'white' }}>
              <ChevronDown size={14} /> {t.liveChat.scrollToBottom}
            </button>
          </div>
        )}

        {/* Quick reactions */}
        <div className="flex items-center gap-1.5 px-4 py-2 overflow-x-auto scrollbar-thin flex-shrink-0"
          style={{ borderTop: '1px solid hsl(222 25% 16%)' }}>
          <SmilePlus size={14} className="text-slate-500 flex-shrink-0" />
          {quickReactions.map((emoji) => (
            <button key={`quick-${emoji}`} onClick={() => handleQuickReaction(emoji)}
              className="text-base hover:scale-125 transition-transform flex-shrink-0 px-1">
              {emoji}
            </button>
          ))}
        </div>

        {/* Input Area */}
        <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
          style={{ borderTop: '1px solid hsl(222 25% 16%)', background: 'hsl(222 28% 13%)' }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: 'hsl(25 95% 53%)', color: 'white' }}>
            FN
          </div>
          <input type="text" value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown}
            placeholder={activeRoom ? `${t.chat.title} ${activeRoom.homeTeam} vs ${activeRoom.awayTeam}...` : t.chat.placeholder}
            className="flex-1 bg-transparent text-sm text-white placeholder-slate-600 outline-none min-w-0" />
          <button onClick={handleSend} disabled={!inputText.trim()}
            className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-150 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: inputText.trim() ? 'hsl(25 95% 53%)' : 'hsl(222 25% 18%)' }}>
            <Send size={15} className={inputText.trim() ? 'text-white' : 'text-slate-600'} />
          </button>
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="hidden xl:flex flex-col gap-4 w-64 2xl:w-72 flex-shrink-0">
        {/* Game Rooms */}
        {roomsLoading ? (
          <div className="rounded-xl p-4 flex items-center justify-center" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
            <div className="w-5 h-5 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
          </div>
        ) : (
          <RoomList rooms={rooms} activeRoomId={activeRoom?.id ?? null} onSelect={setActiveRoom} onSync={handleSyncRooms} syncing={syncing} />
        )}

        {/* Trending Topics */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
          <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
            <TrendingUp size={15} className="text-orange-400" />
            <h3 className="text-sm font-semibold text-white">{t.liveChat.trendingTopics}</h3>
          </div>
          <div className="px-4 py-3 space-y-3">
            {trendingTopics.map((topic, idx) => (
              <div key={topic.id} className="flex items-center justify-between group cursor-pointer">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-600 w-4 tabular-nums">{idx + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-orange-400 group-hover:text-orange-300 transition-colors">{topic.tag}</p>
                    <p className="text-xs text-slate-500 font-mono tabular-nums">{topic.count} tweets</p>
                  </div>
                </div>
                <Hash size={13} className="text-slate-600 group-hover:text-slate-400 transition-colors" />
              </div>
            ))}
          </div>
        </div>

        {/* Chat Activity */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}>
          <div className="px-4 py-3" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
            <h3 className="text-sm font-semibold text-white">{t.liveChat.onlineNow}</h3>
          </div>
          <div className="px-4 py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t.chat.title}</span>
              <span className="font-mono tabular-nums font-semibold text-white">{messages.length.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t.liveChat.gameRooms}</span>
              <span className="font-mono tabular-nums font-semibold text-white">{rooms.filter((r) => LIVE_STATUSES.includes(r.matchStatus)).length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t.common.live}</span>
              <span className="font-mono text-lg">🔥</span>
            </div>
            {isAdmin && (
              <>
                <div style={{ borderTop: '1px solid hsl(222 25% 18%)', paddingTop: '8px', marginTop: '4px' }}>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{t.liveChat.mutedUsers}</span>
                    <span className="font-mono tabular-nums font-semibold" style={{ color: 'hsl(200 80% 60%)' }}>{mutedUsers.length}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}